"use client";

import {
  AddCircleRounded,
  CheckCircleRounded,
  FileUploadRounded,
  LandscapeRounded,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Slide,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentDomain } from "../components/common/utils";
import AdminBottomNavigation from "../components/layouts/AdminBottomNavigation";
import BlogsGrid from "../components/layouts/BlogsGrid";
import ImageUpload from "../components/layouts/ImageUpload";
import Editor from "../components/layouts/rich-text-editor/editor";
import useAuth from "../hooks/useAuth";
import useTostify from "../hooks/useTostify";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AdminBlogs = () => {
  const { setNotification } = useTostify();
  const [menu, setMenu] = useState("/admin/blogs");
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [loadingBlogs, setLoadingBlogs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [edit, setEdit] = useState(false);

  const [blogs, setBlogs] = useState([]);

  //form fields
  const [blogId, setBlogId] = useState("");
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [content2, setContent2] = useState("");
  const [content3, setContent3] = useState("");
  const [status, setStatus] = useState(true);
  const [bannerFiles, setBannerFiles] = useState([]);
  const [coverFiles, setCoverFiles] = useState([]);
  const [bannerImage, setBannerImage] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [errors, setErrors] = useState({});

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleCloseDialog = () => {
    setFormDialogOpen(false);
    clearForm();
    setErrors({});
  };

  const handleEditorChange = (html, fieldName) => {
    switch (fieldName) {
      case "content":
        setContent(html);
        break;
      case "content2":
        setContent2(html);
        break;
      case "content3":
        setContent3(html);
        break;
      default:
        break;
    }
  };

  const handleUploadImage = (uploadFor) => {
    const newErrors = {};
    const formData = new FormData();

    if (uploadFor === "banner") {
      if (bannerFiles.length <= 0) {
        newErrors.bannerImage = "Banner Image is required!!";
        setErrors(newErrors);
        return;
      }
      formData.append("bannerImage", bannerFiles[0]);
      setUploadingBanner(true);
    } else if (uploadFor === "cover") {
      if (coverFiles.length <= 0) {
        newErrors.coverImage = "Cover Image is required!!";
        setErrors(newErrors);
        return;
      }
      formData.append("coverImage", coverFiles[0]);
      setUploadingCover(true);
    }

    // This is a placeholder - in a real app, you'd upload to your server
    // For now, we'll simulate a successful upload
    setTimeout(() => {
      if (uploadFor === "banner") {
        setBannerImage(URL.createObjectURL(bannerFiles[0]));
        setBannerFiles([]);
        setUploadingBanner(false);
        setNotification({
          open: true,
          severity: "success",
          msg: "Banner uploaded successfully!",
        });
      } else if (uploadFor === "cover") {
        setCoverImage(URL.createObjectURL(coverFiles[0]));
        setCoverFiles([]);
        setUploadingCover(false);
        setNotification({
          open: true,
          severity: "success",
          msg: "Cover image uploaded successfully!",
        });
      }
    }, 1000);
  };

  const handleSave = async () => {
    if (!validateBlogForm()) return;

    const blogData = new FormData();
    blogData.append("title", title);
    blogData.append("slug", slug);
    blogData.append("excerpt", excerpt);
    blogData.append("content", content);
    blogData.append("content2", content2 || "");
    blogData.append("content3", content3 || "");
    blogData.append("status", status);

    if (bannerFiles.length > 0) {
      blogData.append("bannerImage", bannerFiles[0]);
    }

    if (coverFiles.length > 0) {
      blogData.append("coverImage", coverFiles[0]);
    }

    setSaving(true);

    try {
      let response;

      if (edit) {
        response = await axios.put(
          `${getCurrentDomain()}/blogs/${blogId}`,
          blogData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        response = await axios.post(`${getCurrentDomain()}/blogs`, blogData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      if (response.data && response.data.success) {
        setNotification({
          open: true,
          severity: "success",
          msg: edit
            ? "Blog updated successfully!"
            : "Blog created successfully!",
        });
        handleCloseDialog();
        reloadBlogs();
      } else {
        throw new Error(response.data?.message || "Operation failed");
      }
    } catch (error) {
      setNotification({
        open: true,
        severity: "error",
        msg: error.message || "An error occurred",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (blog) => {
    if (!blog || !blog._id) return;

    setBlogId(blog._id);
    setTitle(blog.title || "");
    setSlug(blog.slug || "");
    setExcerpt(blog.excerpt || "");
    setContent(blog.content || "");
    setContent2(blog.content2 || "");
    setContent3(blog.content3 || "");
    setStatus(blog.status);
    setBannerImage(blog.bannerImage || "");
    setCoverImage(blog.coverImage || "");

    setEdit(true);
    setFormDialogOpen(true);
  };

  const validateBlogForm = () => {
    let valid = true;
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = "Title is required!";
      valid = false;
    }

    if (!slug.trim()) {
      newErrors.slug = "Slug is required!";
      valid = false;
    } else if (!/^[a-z0-9-]+$/.test(slug)) {
      newErrors.slug =
        "Slug can only contain lowercase letters, numbers, and hyphens";
      valid = false;
    }

    if (!content.trim()) {
      newErrors.content = "Content is required!";
      valid = false;
    }

    if (!edit && !bannerFiles.length && !bannerImage) {
      newErrors.bannerImage = "Banner image is required!";
      valid = false;
    }

    if (!edit && !coverFiles.length && !coverImage) {
      newErrors.coverImage = "Cover image is required!";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const clearForm = () => {
    setBlogId("");
    setTitle("");
    setSlug("");
    setExcerpt("");
    setContent("");
    setContent2("");
    setContent3("");
    setStatus(true);
    setBannerImage("");
    setBannerFiles([]);
    setCoverImage("");
    setCoverFiles([]);
    setEdit(false);
  };

  const reloadBlogs = useCallback(() => {
    setLoadingBlogs(true);
    axios
      .get(`${getCurrentDomain()}/blogs`)
      .then((res) => {
        if (res.data && res.data.success) {
          setBlogs(res.data.data || []);
        } else {
          setBlogs([]);
        }
        setLoadingBlogs(false);
      })
      .catch((err) => {
        setNotification({
          open: true,
          severity: "error",
          msg: "Error while fetching blogs!",
        });
        setBlogs([]);
        setLoadingBlogs(false);
      });
  }, [setNotification]);

  useEffect(() => {
    reloadBlogs();
  }, [reloadBlogs]);

  useEffect(() => {
    if (!isAuthenticated()) navigate("/access-denied");
  }, [isAuthenticated, navigate]);

  // Generate slug from title
  const generateSlug = (text) => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/[^\w-]+/g, "") // Remove all non-word chars
      .replace(/--+/g, "-"); // Replace multiple - with single -
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    if (!edit) {
      setSlug(generateSlug(newTitle));
    }
  };

  return (
    <>
      <Stack direction="column" spacing={2} p={4}>
        <Typography variant="h4" sx={{ textAlign: "center" }}>
          Manage Blogs
        </Typography>
        <BlogsGrid
          blogs={blogs}
          reloadBlogs={reloadBlogs}
          loadingBlogs={loadingBlogs}
          handleOpenEdit={handleOpenEdit}
          editable
        />
      </Stack>
      <Dialog
        open={formDialogOpen}
        fullWidth
        maxWidth="md"
        keepMounted
        TransitionComponent={Transition}
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          {edit ? "Update" : "Add New"} Blog
        </DialogTitle>
        <Divider />
        <DialogContent>
          <p
            onClick={handleCloseDialog}
            style={{
              position: "absolute",
              top: "0",
              color: "white",
              right: "20px",
              background: "#0000008f",
              padding: "1px 8px",
              borderRadius: "100px",
              cursor: "pointer",
            }}
          >
            X
          </p>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    label="Blog Title"
                    name="title"
                    error={!!errors.title}
                    helperText={errors.title}
                    value={title}
                    onChange={handleTitleChange}
                    InputProps={{
                      endAdornment: <LandscapeRounded />,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    label="Blog Slug"
                    name="slug"
                    error={!!errors.slug}
                    helperText={
                      errors.slug || "Used in the URL, e.g., my-blog-post"
                    }
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Excerpt"
                    name="excerpt"
                    multiline
                    rows={2}
                    value={excerpt}
                    onChange={(e) => setExcerpt(e.target.value)}
                    helperText="A short summary of the blog post"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Editor
                    id="blog-content-editor"
                    label="Blog Content *"
                    placeholder="Write your blog content here..."
                    errorMsg={errors.content}
                    content={content}
                    handleChange={(html) => handleEditorChange(html, "content")}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Editor
                    id="blog-content2-editor"
                    label="Additional Content (Optional)"
                    placeholder="Write additional content here..."
                    content={content2}
                    handleChange={(html) =>
                      handleEditorChange(html, "content2")
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Editor
                    id="blog-content3-editor"
                    label="More Content (Optional)"
                    placeholder="Write more content here..."
                    content={content3}
                    handleChange={(html) =>
                      handleEditorChange(html, "content3")
                    }
                  />
                </Grid>

                <Grid item sm={12} md={6}>
                  <ImageUpload
                    files={bannerFiles}
                    setFiles={setBannerFiles}
                    disabled={bannerImage ? true : false}
                    title="Banner Image"
                    size={{ width: 1920, height: 1080 }}
                    errors={errors}
                    setErrors={setErrors}
                    fieldName="bannerImage"
                  />
                </Grid>
                <Grid item sm={12} md={6}>
                  <Stack
                    height="100%"
                    justifyContent="space-around"
                    alignItems="center"
                    spacing={2}
                  >
                    {bannerImage ? (
                      <div>
                        <img
                          src={bannerImage || "/placeholder.svg"}
                          alt="Banner"
                          style={{
                            maxWidth: "100%",
                            height: "auto",
                            borderRadius: "4px",
                          }}
                        />
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setBannerImage("")}
                          sx={{ mt: 1 }}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <Typography component="div" variant="caption">
                        After saving, banner image will show here!
                      </Typography>
                    )}
                    <LoadingButton
                      startIcon={<FileUploadRounded />}
                      color="secondary"
                      variant="contained"
                      size="small"
                      loading={uploadingBanner}
                      onClick={() => handleUploadImage("banner")}
                      disabled={bannerImage ? true : false}
                    >
                      Upload
                    </LoadingButton>
                  </Stack>
                </Grid>
                <Grid item sm={12} md={6}>
                  <ImageUpload
                    files={coverFiles}
                    setFiles={setCoverFiles}
                    title="Cover Image"
                    size={{ width: 400, height: 250 }}
                    disabled={coverImage ? true : false}
                    errors={errors}
                    setErrors={setErrors}
                    fieldName="coverImage"
                  />
                </Grid>
                <Grid item sm={12} md={6}>
                  <Stack
                    height="100%"
                    justifyContent="space-around"
                    alignItems="center"
                  >
                    {coverImage ? (
                      <div>
                        <img
                          src={coverImage || "/placeholder.svg"}
                          alt="Cover"
                          style={{
                            maxWidth: "100%",
                            height: "auto",
                            borderRadius: "4px",
                          }}
                        />
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setCoverImage("")}
                          sx={{ mt: 1 }}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <Typography component="div" variant="caption">
                        After saving, cover image will show here!
                      </Typography>
                    )}
                    <LoadingButton
                      startIcon={<FileUploadRounded />}
                      color="secondary"
                      variant="contained"
                      size="small"
                      loading={uploadingCover}
                      onClick={() => handleUploadImage("cover")}
                      disabled={coverImage ? true : false}
                    >
                      Upload
                    </LoadingButton>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>

            <CardActions>
              <Button variant="outlined" fullWidth onClick={handleCloseDialog}>
                Cancel
              </Button>
              <LoadingButton
                loading={saving}
                loadingPosition="start"
                fullWidth
                startIcon={<CheckCircleRounded />}
                variant="contained"
                onClick={handleSave}
              >
                Save
              </LoadingButton>
            </CardActions>
          </Card>
        </DialogContent>
      </Dialog>

      <Tooltip title="Add a new Blog">
        <Button
          variant="contained"
          startIcon={<AddCircleRounded />}
          sx={{
            borderRadius: 5,
            position: "fixed",
            zIndex: 1,
            bottom: 100,
            margin: "auto",
            left: "50%",
            transform: "translateX(-50%)",
          }}
          onClick={() => setFormDialogOpen(true)}
        >
          Add New Blog
        </Button>
      </Tooltip>
      <AdminBottomNavigation menu={menu} setMenu={setMenu} />
    </>
  );
};

export default AdminBlogs;
